<template>
  <main>
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div class="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <p class="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-700">Internal</p>
          <h1 class="mt-2 text-3xl font-bold text-gray-900">Feature audit</h1>
          <p class="mt-2 max-w-3xl text-sm text-gray-600">
            Current feature footprint plus historical tracked activity across the full database.
          </p>
        </div>

        <div class="flex flex-wrap items-center gap-3">
          <div class="rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm text-gray-600 shadow-sm">
            <span class="font-semibold text-gray-900">Since</span>
            {{ formatDate(report.data_since) || 'Unknown' }}
          </div>
          <button
            type="button"
            class="inline-flex items-center rounded-xl border-2 border-b-4 border-emerald-700 bg-emerald-200 px-4 py-2 text-sm font-bold text-emerald-800 hover:bg-emerald-100 disabled:opacity-60"
            style="box-shadow: inset 0px -6px 0px #6EE7B7, inset 0 -7px 0 0 #ECFDF5, inset 0px 1px 0px 1px #ECFDF5;"
            :disabled="loading"
            @click="loadReport"
          >
            {{ loading ? 'Refreshing...' : 'Refresh report' }}
          </button>
        </div>
      </div>

      <div v-if="errorMessage" class="mt-6 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
        {{ errorMessage }}
      </div>

      <div v-if="loading" class="mt-8">
        <Loading />
      </div>

      <template v-else>
        <div class="mt-8 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <section class="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
            <p class="text-sm text-gray-500">Users</p>
            <p class="mt-2 text-3xl font-bold text-gray-900">{{ formatNumber(report.overview.total_users) }}</p>
            <p class="mt-2 text-sm text-gray-600">{{ formatNumber(report.overview.users_with_tracking) }} have tracked activity.</p>
          </section>

          <section class="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
            <p class="text-sm text-gray-500">Boards + blocks</p>
            <p class="mt-2 text-3xl font-bold text-gray-900">{{ formatNumber(report.overview.total_boards) }} / {{ formatNumber(report.overview.total_blocks) }}</p>
            <p class="mt-2 text-sm text-gray-600">{{ formatNumber(report.overview.users_with_boards) }} users with boards.</p>
          </section>

          <section class="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
            <p class="text-sm text-gray-500">Tracked events</p>
            <p class="mt-2 text-3xl font-bold text-gray-900">{{ formatNumber(report.overview.total_tracking_events) }}</p>
            <p class="mt-2 text-sm text-gray-600">Historical activity by block type and action.</p>
          </section>

          <section class="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
            <p class="text-sm text-gray-500">Unused features</p>
            <p class="mt-2 text-3xl font-bold text-gray-900">{{ formatNumber(report.unused_features.length) }}</p>
            <p class="mt-2 text-sm text-gray-600">Predefined features with zero observed users.</p>
          </section>
        </div>

        <div class="mt-8 grid gap-6 xl:grid-cols-[1.35fr_minmax(0,1fr)]">
          <section class="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
            <div class="flex items-center justify-between gap-3">
              <div>
                <h2 class="text-lg font-semibold text-gray-900">Feature adoption</h2>
                <p class="text-sm text-gray-500">Sorted by distinct users.</p>
              </div>
            </div>

            <div class="mt-5 grid gap-6 lg:grid-cols-2">
              <div>
                <h3 class="text-sm font-semibold uppercase tracking-wide text-gray-500">Board features</h3>
                <div class="mt-3 space-y-3">
                  <div v-for="feature in report.board_features" :key="feature.key" class="rounded-xl border px-4 py-3"
                    :class="feature.users === 0 ? 'border-red-200 bg-red-50' : 'border-gray-200 bg-gray-50'">
                    <div class="flex items-start justify-between gap-3">
                      <div>
                        <p class="font-medium text-gray-900">{{ feature.label }}</p>
                        <p class="mt-1 text-sm text-gray-600">{{ formatNumber(feature.users) }} users, {{ formatNumber(feature.entities) }} boards</p>
                      </div>
                      <span class="rounded-full px-2.5 py-1 text-xs font-semibold"
                        :class="feature.users === 0 ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'">
                        {{ percentOfUsers(feature.users) }}
                      </span>
                    </div>
                    <p class="mt-2 text-xs text-gray-500">Last seen {{ formatDate(feature.last_used_at) || 'never' }}</p>
                  </div>
                </div>
              </div>

              <div>
                <h3 class="text-sm font-semibold uppercase tracking-wide text-gray-500">Block features</h3>
                <div class="mt-3 space-y-3">
                  <div v-for="feature in report.block_features" :key="feature.key" class="rounded-xl border px-4 py-3"
                    :class="feature.users === 0 ? 'border-red-200 bg-red-50' : 'border-gray-200 bg-gray-50'">
                    <div class="flex items-start justify-between gap-3">
                      <div>
                        <p class="font-medium text-gray-900">{{ feature.label }}</p>
                        <p class="mt-1 text-sm text-gray-600">{{ formatNumber(feature.users) }} users, {{ formatNumber(feature.entities) }} blocks</p>
                      </div>
                      <span class="rounded-full px-2.5 py-1 text-xs font-semibold"
                        :class="feature.users === 0 ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'">
                        {{ percentOfUsers(feature.users) }}
                      </span>
                    </div>
                    <p class="mt-2 text-xs text-gray-500">Last seen {{ formatDate(feature.last_used_at) || 'never' }}</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section class="space-y-6">
            <div class="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
              <h2 class="text-lg font-semibold text-gray-900">Candidates to archive</h2>
              <p class="mt-1 text-sm text-gray-500">Features with zero observed users in the current audit rules.</p>
              <div v-if="report.unused_features.length" class="mt-4 flex flex-wrap gap-2">
                <span v-for="feature in report.unused_features" :key="feature.key" class="rounded-full bg-red-100 px-3 py-1 text-sm font-medium text-red-700">
                  {{ feature.label }}
                </span>
              </div>
              <p v-else class="mt-4 text-sm text-gray-600">Every tracked feature has at least one user.</p>
            </div>

            <div class="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
              <h2 class="text-lg font-semibold text-gray-900">Subscription mix</h2>
              <div class="mt-4 space-y-3">
                <div v-for="entry in report.overview.subscription_status_breakdown" :key="entry.key" class="flex items-center justify-between rounded-xl bg-gray-50 px-4 py-3 text-sm">
                  <span class="font-medium text-gray-700">{{ prettifyKey(entry.key) }}</span>
                  <span class="font-semibold text-gray-900">{{ formatNumber(entry.count) }}</span>
                </div>
              </div>
            </div>

            <div class="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
              <h2 class="text-lg font-semibold text-gray-900">Notes</h2>
              <div class="mt-4 space-y-2 text-sm text-gray-600">
                <p v-for="note in report.notes" :key="note">{{ note }}</p>
                <p>Generated {{ formatDateTime(report.generated_at) || 'just now' }}.</p>
              </div>
            </div>
          </section>
        </div>

        <div class="mt-8 grid gap-6 xl:grid-cols-2">
          <section class="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm overflow-hidden">
            <h2 class="text-lg font-semibold text-gray-900">Block types</h2>
            <p class="mt-1 text-sm text-gray-500">Current footprint and historical tracking.</p>
            <div class="mt-4 overflow-x-auto">
              <table class="min-w-full divide-y divide-gray-200 text-sm">
                <thead class="bg-gray-50 text-left text-gray-500">
                  <tr>
                    <th class="px-3 py-2 font-semibold">Type</th>
                    <th class="px-3 py-2 font-semibold">Current users</th>
                    <th class="px-3 py-2 font-semibold">Current blocks</th>
                    <th class="px-3 py-2 font-semibold">Tracked users</th>
                    <th class="px-3 py-2 font-semibold">Events</th>
                    <th class="px-3 py-2 font-semibold">Last activity</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-gray-100 bg-white">
                  <tr v-for="row in report.block_types" :key="row.key">
                    <td class="px-3 py-3 font-medium text-gray-900">{{ row.label }}</td>
                    <td class="px-3 py-3 text-gray-600">{{ formatNumber(row.current_users) }}</td>
                    <td class="px-3 py-3 text-gray-600">{{ formatNumber(row.current_blocks) }}</td>
                    <td class="px-3 py-3 text-gray-600">{{ formatNumber(row.tracked_users) }}</td>
                    <td class="px-3 py-3 text-gray-600">{{ formatNumber(row.tracking_events) }}</td>
                    <td class="px-3 py-3 text-gray-600">{{ formatDate(row.last_activity_at) || 'Never' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section class="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm overflow-hidden">
            <h2 class="text-lg font-semibold text-gray-900">Tracked actions</h2>
            <p class="mt-1 text-sm text-gray-500">What people actually do over time.</p>
            <div class="mt-4 overflow-x-auto">
              <table class="min-w-full divide-y divide-gray-200 text-sm">
                <thead class="bg-gray-50 text-left text-gray-500">
                  <tr>
                    <th class="px-3 py-2 font-semibold">Action</th>
                    <th class="px-3 py-2 font-semibold">Users</th>
                    <th class="px-3 py-2 font-semibold">Events</th>
                    <th class="px-3 py-2 font-semibold">Last activity</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-gray-100 bg-white">
                  <tr v-for="row in report.element_types" :key="row.key">
                    <td class="px-3 py-3 font-medium text-gray-900">{{ row.label }}</td>
                    <td class="px-3 py-3 text-gray-600">{{ formatNumber(row.users) }}</td>
                    <td class="px-3 py-3 text-gray-600">{{ formatNumber(row.events) }}</td>
                    <td class="px-3 py-3 text-gray-600">{{ formatDate(row.last_activity_at) || 'Never' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
        </div>

        <div class="mt-8 grid gap-6 xl:grid-cols-2">
          <section class="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
            <h2 class="text-lg font-semibold text-gray-900">Product surfaces</h2>
            <div class="mt-4 grid gap-3 sm:grid-cols-2">
              <div class="rounded-xl bg-gray-50 px-4 py-3">
                <p class="text-sm text-gray-500">Projects</p>
                <p class="mt-1 text-2xl font-bold text-gray-900">{{ formatNumber(report.overview.users_with_projects) }}</p>
                <p class="text-sm text-gray-600">users, {{ formatNumber(report.overview.total_projects) }} total projects</p>
              </div>
              <div class="rounded-xl bg-gray-50 px-4 py-3">
                <p class="text-sm text-gray-500">Status pages</p>
                <p class="mt-1 text-2xl font-bold text-gray-900">{{ formatNumber(report.overview.users_with_status_pages) }}</p>
                <p class="text-sm text-gray-600">users, {{ formatNumber(report.overview.users_with_active_status_pages) }} active</p>
              </div>
              <div class="rounded-xl bg-gray-50 px-4 py-3">
                <p class="text-sm text-gray-500">Onboarding answers</p>
                <p class="mt-1 text-2xl font-bold text-gray-900">{{ formatNumber(report.overview.total_onboarding_answers) }}</p>
                <p class="text-sm text-gray-600">{{ formatNumber(report.overview.users_with_completed_onboarding) }} users completed</p>
              </div>
              <div class="rounded-xl bg-gray-50 px-4 py-3">
                <p class="text-sm text-gray-500">Archived projects</p>
                <p class="mt-1 text-2xl font-bold text-gray-900">{{ formatNumber(report.overview.users_with_archived_projects) }}</p>
                <p class="text-sm text-gray-600">users have archived at least one project</p>
              </div>
            </div>
          </section>

          <section class="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm overflow-hidden">
            <h2 class="text-lg font-semibold text-gray-900">Onboarding use cases</h2>
            <div class="mt-4 overflow-x-auto">
              <table class="min-w-full divide-y divide-gray-200 text-sm">
                <thead class="bg-gray-50 text-left text-gray-500">
                  <tr>
                    <th class="px-3 py-2 font-semibold">Use case</th>
                    <th class="px-3 py-2 font-semibold">Users</th>
                    <th class="px-3 py-2 font-semibold">Submissions</th>
                    <th class="px-3 py-2 font-semibold">Completed</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-gray-100 bg-white">
                  <tr v-for="row in report.onboarding_use_cases" :key="row.key">
                    <td class="px-3 py-3 font-medium text-gray-900">{{ prettifyKey(row.key) }}</td>
                    <td class="px-3 py-3 text-gray-600">{{ formatNumber(row.users) }}</td>
                    <td class="px-3 py-3 text-gray-600">{{ formatNumber(row.submissions) }}</td>
                    <td class="px-3 py-3 text-gray-600">{{ formatNumber(row.completed) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
        </div>
      </template>
    </div>
  </main>
</template>

<script>
  import { mapGetters } from 'vuex';
  import Loading from '../components/Loading.vue';
  import { getSupabaseSession } from '../helpers/init-db';
  import { buildEdgeFunctionHeaders, buildEdgeFunctionUrl } from '../helpers/edge-functions';

  const numberFormatter = new Intl.NumberFormat('en-US');
  const shortDateFormatter = new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
  const dateTimeFormatter = new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

  function createEmptyReport() {
    return {
      generated_at: null,
      data_since: null,
      overview: {
        total_users: 0,
        super_users: 0,
        total_boards: 0,
        total_blocks: 0,
        total_projects: 0,
        total_status_pages: 0,
        total_onboarding_answers: 0,
        total_tracking_events: 0,
        users_with_boards: 0,
        users_with_blocks: 0,
        users_with_projects: 0,
        users_with_tracking: 0,
        users_with_status_pages: 0,
        users_with_completed_onboarding: 0,
        users_with_archived_projects: 0,
        users_with_active_status_pages: 0,
        subscription_status_breakdown: [],
      },
      board_features: [],
      block_features: [],
      block_types: [],
      element_types: [],
      onboarding_use_cases: [],
      unused_features: [],
      notes: [],
    };
  }

  export default {
    name: 'FeatureAudit',
    components: {
      Loading,
    },
    data() {
      return {
        loading: false,
        errorMessage: '',
        report: createEmptyReport(),
      };
    },
    computed: {
      ...mapGetters('auth', ['currentUser']),
    },
    mounted() {
      this.loadReport();
    },
    methods: {
      async loadReport() {
        this.loading = true;
        this.errorMessage = '';

        try {
          const session = await getSupabaseSession();
          const accessToken = session?.access_token;

          if (!accessToken) {
            throw new Error('Authentication error. Please sign in again.');
          }

          const response = await fetch(buildEdgeFunctionUrl('feature-audit'), {
            headers: buildEdgeFunctionHeaders({ accessToken }),
          });

          const data = await response.json().catch(() => null);
          if (!response.ok) {
            throw new Error(data?.error || 'Unable to load feature audit.');
          }

          this.report = {
            ...createEmptyReport(),
            ...(data || {}),
          };
        } catch (error) {
          console.error(error);
          this.errorMessage = error?.message || 'Unable to load feature audit.';
        } finally {
          this.loading = false;
        }
      },
      formatNumber(value) {
        return numberFormatter.format(Number(value || 0));
      },
      formatDate(value) {
        if (!value) {
          return null;
        }

        const date = new Date(value);
        if (Number.isNaN(date.getTime())) {
          return null;
        }

        return shortDateFormatter.format(date);
      },
      formatDateTime(value) {
        if (!value) {
          return null;
        }

        const date = new Date(value);
        if (Number.isNaN(date.getTime())) {
          return null;
        }

        return dateTimeFormatter.format(date);
      },
      percentOfUsers(userCount) {
        const totalUsers = Number(this.report?.overview?.total_users || 0);
        if (!totalUsers) {
          return '0%';
        }

        return `${Math.round((Number(userCount || 0) / totalUsers) * 100)}%`;
      },
      prettifyKey(value) {
        if (!value) {
          return 'Unknown';
        }

        return String(value)
          .replace(/_/g, ' ')
          .replace(/\b\w/g, (character) => character.toUpperCase());
      },
    },
  };
</script>